<template>
    <div class="flex flex-row justify-between m-4">
        <div class="bg-default-lighter-gray p-3 w-1/2">
            <div class="text-sm font-bold border-b border-default-gray mb-2" data-cy="intermediaries-sub-title-assign">
                {{ $t('company.intermediaries.assign-intermediaries') }}
            </div>
            <div class="w-full">
                <div class="mb-4">{{ $t('company.intermediaries.description') }}</div>
                <div>{{ $t('company.intermediaries.tables.assign.search-title') }}</div>
                <div class="flex flex-col lg:flex-row justify-between">
                    <div class="w-full lg:w-1/2">
                        <a-input-search
                            data-cy="assign-intermediaries-input-search"
                            @search="handleAssignSearch"
                            :placeholder="$t('company.intermediaries.tables.assign.search')"
                        />
                    </div>
                    <div>
                        <a-button data-cy="assign-selected-btn" type="primary" @click="assignSelected">
                            {{ $t('company.intermediaries.tables.assign.assign-selected-btn') }}
                        </a-button>
                    </div>
                </div>
                <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
                    <div class="w-full">
                        <a-table
                            data-cy="assign-intermediaries-table"
                            :columns="getAssignColumns"
                            :dataSource="searchAssign"
                            :rowKey="(record) => record.id"
                            :pagination="assign.pagination"
                            :locale="assign.locale"
                            :loading="loading"
                            :rowSelection="{
                                selectedRowKeys: assign.selected_row_keys,
                                onChange: assignOnSelectChange,
                            }"
                            size="middle"
                            class="ant-table-white"
                        >
                            <template #bodyCell="{ column, record, text }">
                                <template v-if="column.key === 'actions'">
                                    <a-button-group>
                                        <a-tooltip
                                            placement="top"
                                            :title="$t('company.intermediaries.tables.assign.actions.assign')"
                                        >
                                            <a-button
                                                :disabled="assignRecordsSelected"
                                                type="primary"
                                                data-cy="assign-button"
                                                @click="assignIntermediary(record)"
                                            >
                                                <RightOutlined />
                                            </a-button>
                                        </a-tooltip>
                                    </a-button-group>
                                </template>
                                <template v-else>
                                    {{ text }}
                                </template>
                            </template>
                        </a-table>
                    </div>
                </div>
            </div>
        </div>
        <div class="px-3 pt-3 w-1/2">
            <div class="text-sm font-bold" data-cy="intermediaries-sub-title-assigned">
                {{ $t('company.intermediaries.assigned-intermediaries') }}
            </div>
            <div class="flex flex-col lg:flex-row justify-between">
                <div class="w-full lg:w-1/2">
                    <a-input-search
                        data-cy="assigned-intermediaries-input-search"
                        @search="handleAssignedSearch"
                        :placeholder="$t('company.intermediaries.tables.assigned.search')"
                    />
                </div>
                <div>
                    <a-button data-cy="unassign-selected-btn" @click="removeSelected">
                        {{ $t('company.intermediaries.tables.assigned.unassign-selected-btn') }}
                    </a-button>
                </div>
            </div>
            <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
                <div class="w-full">
                    <a-table
                        data-cy="assigned-intermediaries-table"
                        :columns="getAssignedColumns"
                        :dataSource="searchAssigned"
                        :rowKey="(record) => record.company_intermediary_id"
                        :pagination="assigned.pagination"
                        :locale="assigned.locale"
                        :loading="loading"
                        :rowSelection="{
                            selectedRowKeys: assigned.selected_row_keys,
                            onChange: assignedOnSelectChange,
                        }"
                        size="middle"
                        class="ant-table-white"
                    >
                        <template #bodyCell="{ column, record, text }">
                            <template v-if="column.key === 'actions'">
                                <a-button-group>
                                    <a-tooltip
                                        placement="top"
                                        :title="$t('company.intermediaries.tables.assigned.actions.unassign')"
                                    >
                                        <a-button
                                            :disabled="assignedRecordsSelected"
                                            danger
                                            data-cy="assigned-button"
                                            @click="removeIntermediary(record)"
                                        >
                                            <LeftOutlined />
                                        </a-button>
                                    </a-tooltip>
                                </a-button-group>
                            </template>
                            <template v-else-if="column.key === 'print_cert'">
                                <a-checkbox
                                    @click="refresh"
                                    v-model:checked="
                                        searchAssigned[
                                            searchAssigned.findIndex(
                                                (x) => x.company_intermediary_id === record.company_intermediary_id,
                                            )
                                        ].is_print_certificate
                                    "
                                />
                            </template>
                            <template v-else-if="column.key === 'print_cover'">
                                <a-checkbox
                                    @click="refresh"
                                    v-model:checked="
                                        searchAssigned[
                                            searchAssigned.findIndex(
                                                (x) => x.company_intermediary_id === record.company_intermediary_id,
                                            )
                                        ].is_print_covernote
                                    "
                                />
                            </template>
                            <template v-else-if="column.key === 'cover_count'">
                                <a-input-number
                                    @change="refresh"
                                    v-model:value="
                                        searchAssigned[
                                            searchAssigned.findIndex(
                                                (x) => x.company_intermediary_id === record.company_intermediary_id,
                                            )
                                        ].print_covernote_count
                                    "
                                    :min="0"
                                    :max="100"
                                />
                            </template>
                            <template v-else>
                                {{ text }}
                            </template>
                        </template>
                    </a-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue';

export default {
    components: { LeftOutlined, RightOutlined },
    data() {
        return {
            loading: false,
            assign: {
                data: [],
                search_value: '',
                pagination: {
                    pageSize: 10,
                    showSizeChanger: false,
                    total: 0,
                },
                locale: {
                    emptyText: this.$t('globals.tables.no_data'),
                },
                processing: false,
                current_filter: {},
                sorter: {},
                params: {},
                selected_row_keys: [],
                select_all: false,
            },
            assigned: {
                data: [],
                search_value: '',
                pagination: {
                    pageSize: 10,
                    showSizeChanger: false,
                    total: 0,
                },
                locale: {
                    emptyText: this.$t('globals.tables.no_data'),
                },
                processing: false,
                current_filter: {},
                sorter: {},
                params: {},
                selected_row_keys: [],
                select_all: false,
            },
        };
    },
    props: {
        company_id: String,
        is_edit: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {
        this.fetch();
    },
    computed: {
        getAssignColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('globals.tables.assign-intermediaries.headers.intermediaries'),
                    key: 'intermediaries',
                    dataIndex: 'name',
                },
            ];
        },
        getAssignedColumns() {
            return [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('globals.tables.assign-intermediaries.headers.intermediaries'),
                    key: 'intermediaries',
                    dataIndex: 'name',
                },
                {
                    title: this.$t('globals.tables.assign-intermediaries.headers.print_cert'),
                    key: 'print_cert',
                    dataIndex: 'is_print_certificate',
                },
                {
                    title: this.$t('globals.tables.assign-intermediaries.headers.print_cover'),
                    key: 'print_cover',
                    dataIndex: 'is_print_covernote',
                },
                {
                    title: this.$t('globals.tables.assign-intermediaries.headers.cover_count'),
                    key: 'cover_count',
                    dataIndex: 'print_covernote_count',
                },
            ];
        },
        assignRecordsSelected() {
            return this.assign.selected_row_keys.length > 0;
        },
        assignedRecordsSelected() {
            return this.assigned.selected_row_keys.length > 0;
        },
        searchAssign() {
            if (this.assign.search_value.length == 0) {
                return this.assign.data;
            } else {
                this.assignOnSelectChange([]);
                return this.assign.data.filter((x) =>
                    x.name.toLowerCase().includes(this.assign.search_value.toLowerCase()),
                );
            }
        },
        searchAssigned() {
            if (this.assigned.search_value.length == 0) {
                return this.assigned.data;
            } else {
                this.assignedOnSelectChange([]);
                return this.assigned.data.filter((x) =>
                    x.name.toLowerCase().includes(this.assigned.search_value.toLowerCase()),
                );
            }
        },
    },
    methods: {
        fetch() {
            this.loading = true;

            this.$http
                .get('/api/intermediaries/' + this.company_id)
                .then((response) => {
                    this.assign.data = response.data.intermediaries;
                    this.assigned.data = response.data.assigned_intermediaries.map((intermediary) => {
                        return {
                            ...intermediary,
                            is_print_certificate: intermediary.is_print_certificate ? true : false,
                            is_print_covernote: intermediary.is_print_covernote ? true : false,
                        };
                    });
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CIAI01' }),
                    });
                })
                .finally(() => {
                    this.loading = false;
                    this.$emit('is_edit', false);
                });
        },
        assignOnSelectChange(selected_row_keys, selected_rows) {
            this.assign.selected_row_keys = selected_row_keys;
        },
        assignedOnSelectChange(selected_row_keys, selected_rows) {
            this.assigned.selected_row_keys = selected_row_keys;
        },
        handleAssignSearch(value) {
            this.assign.search_value = value;
        },
        handleAssignedSearch(value) {
            this.assigned.search_value = value;
        },
        assignIntermediary(value) {
            this.assigned.data[this.assigned.data.length] = {
                name: value.name,
                company_id: this.company_id,
                company_intermediary_id: value.id,
                is_print_certificate: false,
                is_print_covernote: false,
                print_covernote_count: 0,
            };

            this.assign.data = this.assign.data
                .filter((x) => x.id != value.id)
                .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
            this.assigned.data = this.assigned.data.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));

            this.$emit('is_edit', true);
        },
        removeIntermediary(value) {
            this.assign.data[this.assign.data.length] = {
                id: value.company_intermediary_id,
                name: value.name,
            };

            this.assigned.data = this.assigned.data
                .filter((x) => x.company_intermediary_id != value.company_intermediary_id)
                .sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
            this.assign.data = this.assign.data.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));

            this.$emit('is_edit', true);
        },
        assignSelected() {
            let rows = this.assign.data.filter((x) => this.assign.selected_row_keys.includes(x.id));
            this.assignOnSelectChange([]);
            for (let value in rows) {
                this.assignIntermediary(rows[value]);
            }
        },
        removeSelected() {
            let rows = this.assigned.data.filter((x) =>
                this.assigned.selected_row_keys.includes(x.company_intermediary_id),
            );
            this.assignedOnSelectChange([]);
            for (let value in rows) {
                this.removeIntermediary(rows[value]);
            }
        },
        refresh() {
            setTimeout(() => {
                let data = this.assigned.data.splice(this.assigned.data.length);
                this.assigned.data.concat([data]);
            }, 100);
            this.$emit('is_edit', true);
        },
        reload() {
            this.fetch();
        },
    },
};
</script>
